import { Alert } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';

import dayjs from 'dayjs';
import { Helmet } from 'react-helmet';

import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { News, useNews } from 'api/news';
import { Box, Button, CustomBox, GridChild, GridContainer, Title } from 'common';
import { NewsCard } from 'widgets/cards';

import { useLanguage } from '../../context/LanguageContext'
import { singleNewsLabels } from '../../locales/translations'

import { NewsImagesCarousel } from './NewsImagesCarousel'

export const SingleNewsPage = () => {
    const { id } = useParams();
    const { onGetOneNews, onGetAllNews } = useNews();

    const [newsDetails, setNewsDetails] = useState<News | null>(null);
    const [latestNews, setLatestNews] = useState<News[]>([]);
    const [loadingDetails, setLoadingDetails] = useState(true);
    const [loadingLatestNews, setLoadingLatestNews] = useState(true);
    const [error, setError] = useState<string>('');

    const { language } = useLanguage();

    const fetchNewsDetails = useCallback(async () => {
        try {
            const result = await onGetOneNews(Number(id));
            if (result.success) {
                setNewsDetails(result.data);
            } else {
                setError('Не удалось загрузить детали новости.');
            }
        } catch (error) {
            setError('Произошла ошибка при запросе деталей новости.');
        } finally {
            setLoadingDetails(false);
        }
    }, [id, onGetOneNews]);

    const fetchLatestNews = useCallback(async () => {
        try {
            const result = await onGetAllNews();
            if (result.success) {
                const sortedNews = result.data.sort((a: News, b: News) => 
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                );
                setLatestNews(sortedNews);
            } else {
                setError('Произошла ошибка при загрузке списка новостей.');
            }
        } catch (error) {
            setError('Произошла ошибка при запросе списка новостей.');
        } finally {
            setLoadingLatestNews(false);
        }
    }, [onGetAllNews]);

    useEffect(() => {
        fetchNewsDetails();
        fetchLatestNews();
    }, [fetchNewsDetails, fetchLatestNews]);

    if (loadingDetails && loadingLatestNews)
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );

    if (error) return <Alert severity="error">{error}</Alert>;
    

    return (
        <Box>
            <GridContainer>
                <GridChild md={8}>
                    {newsDetails && (
                        <CustomBox title={newsDetails[`title_${language}`]}>
                            <Helmet>
                                <title>{newsDetails[`title_${language}`]}</title>
                                <meta name="description" content={newsDetails[`description_${language}`]} />
                            </Helmet>
                            {newsDetails.images && newsDetails.images.length > 0 && newsDetails.images[0] ? (
                                <NewsImagesCarousel newsItem={newsDetails} />
                            ) : (<ImageNotSupportedIcon
                                sx={{
                                    height: 200,
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '5rem'
                                }}
                            />)}
                            <Title gutterBottom variant="h5" sx={{ fontWeight: 600, marginTop: 2 }}>
                                {newsDetails[`title_${language}`]}
                            </Title>
                            <Title gutterBottom variant="body2" color="text.secondary">
                                <div dangerouslySetInnerHTML={{ __html: newsDetails[`description_${language}`] }} />
                            </Title>
                            <Title variant="body2" color="text.primary">
                                {dayjs(newsDetails.created_at).format('DD.MM.YYYY')}
                            </Title>
                        </CustomBox>
                    )}
                </GridChild>

                <GridChild md={4}>
                    {!loadingLatestNews && (
                        <CustomBox title={singleNewsLabels.latestNews[language]}>
                            {latestNews.slice(0, 3).map((newsItem) => (
                                <NewsCard key={newsItem.id} newsItem={newsItem} />
                            ))}

                            <Box sx={{ paddingTop: 6, display: 'flex', justifyContent: 'center' }}>
                                <Button variant="contained" color="error" href="/news">
                                    {singleNewsLabels.allNewsButton[language]}
                                </Button>
                            </Box>
                        </CustomBox>
                    )}
                </GridChild>
            </GridContainer>
        </Box>
    );
};
